import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Dumbbell, Coffee, Check, Trash2, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, useContent } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Calendar() {
  const { data: workouts } = useContent("workouts");
  const now = new Date();
  const [cur, setCur] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [map, setMap] = useState({});
  const [sel, setSel] = useState(null);
  const [choice, setChoice] = useState("");
  const todayStr = iso(now.getFullYear(), now.getMonth(), now.getDate());

  const load = () => api.get("/me/schedule").then((r) => {
    const mp = {}; r.data.forEach((s) => { mp[s.date] = s; }); setMap(mp);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const daysInMonth = new Date(cur.y, cur.m + 1, 0).getDate();
  const firstDay = new Date(cur.y, cur.m, 1).getDay();
  const monthName = new Date(cur.y, cur.m, 1).toLocaleString("default", { month: "long", year: "numeric" });
  const shift = (d) => setCur(({ y, m }) => { const nm = m + d; return { y: y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });

  const openDay = (dateStr) => { setSel(dateStr); setChoice(map[dateStr]?.type === "rest" ? "rest" : map[dateStr]?.workout_id || ""); };

  const save = async () => {
    if (!choice) { toast.error("Pick a workout or rest day."); return; }
    if (choice === "rest") await api.post("/me/schedule", { date: sel, type: "rest" });
    else { const w = workouts.find((x) => x.id === choice); await api.post("/me/schedule", { date: sel, type: "workout", workout_id: w.id, workout_title: w.title }); }
    toast.success("Scheduled!"); setSel(null); load();
  };
  const clear = async () => { await api.delete(`/me/schedule/${sel}`); setSel(null); load(); };
  const toggleDone = async (dateStr) => { await api.patch(`/me/schedule/${dateStr}`); load(); };

  const todayPlan = map[todayStr];

  return (
    <div data-testid="calendar-page" className="px-4 md:px-8 pb-16 pt-2">
      <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Workout Calendar</h1>
      <p className="text-muted-foreground mt-1">Plan your week, schedule rest days and stay on track.</p>

      {todayPlan && (
        <div className="mt-5 flex items-center gap-3 border border-primary/40 bg-primary/5 rounded-xl p-4" data-testid="today-reminder">
          <Bell className="text-primary shrink-0" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-primary font-bold">Today's Plan</div>
            <div className="font-bold">{todayPlan.type === "rest" ? "Rest & Recover 💤" : todayPlan.workout_title}</div>
          </div>
          {todayPlan.type !== "rest" && <button onClick={() => toggleDone(todayStr)} className={`px-4 py-2 rounded-full text-xs uppercase font-bold ${todayPlan.done ? "bg-secondary text-muted-foreground" : "bg-primary text-primary-foreground"}`}>{todayPlan.done ? "Done ✓" : "Mark Done"}</button>}
        </div>
      )}

      <div className="border border-border rounded-xl bg-card p-4 md:p-6 mt-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="grid place-items-center w-10 h-10 rounded-full border border-border hover:border-primary transition-colors"><ChevronLeft size={18} /></button>
          <h2 className="font-display font-800 uppercase text-2xl tracking-tight">{monthName}</h2>
          <button onClick={() => shift(1)} data-testid="cal-next" className="grid place-items-center w-10 h-10 rounded-full border border-border hover:border-primary transition-colors"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 md:gap-2">
          {WD.map((d) => <div key={d} className="text-center text-[11px] uppercase tracking-wide text-muted-foreground font-bold py-2">{d}</div>)}
          {Array.from({ length: firstDay }).map((_, i) => <div key={`b${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1; const dateStr = iso(cur.y, cur.m, day); const entry = map[dateStr]; const isToday = dateStr === todayStr;
            return (
              <button key={day} onClick={() => openDay(dateStr)} data-testid="cal-day"
                className={`aspect-square rounded-lg border p-1.5 flex flex-col text-left transition-colors ${isToday ? "border-primary" : "border-border"} hover:border-primary`}>
                <span className={`text-xs font-bold ${isToday ? "text-primary" : ""}`}>{day}</span>
                {entry && (
                  <span className={`mt-auto text-[9px] md:text-[10px] leading-tight rounded px-1 py-0.5 truncate ${entry.type === "rest" ? "bg-secondary text-muted-foreground" : entry.done ? "bg-primary/20 text-primary" : "bg-primary text-primary-foreground"}`}>
                    {entry.type === "rest" ? "Rest" : entry.workout_title}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-sm" data-testid="cal-dialog">
          <DialogHeader><DialogTitle className="font-display font-800 uppercase text-2xl tracking-tight">Plan {sel}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger data-testid="cal-select"><SelectValue placeholder="Choose workout or rest" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rest"><span className="flex items-center gap-2"><Coffee size={14} /> Rest Day</span></SelectItem>
                {workouts.map((w) => <SelectItem key={w.id} value={w.id}><span className="flex items-center gap-2"><Dumbbell size={14} /> {w.title}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <button onClick={save} data-testid="cal-save" className="flex-1 bg-primary text-primary-foreground py-3 rounded-full text-xs uppercase font-bold flex items-center justify-center gap-1.5"><Check size={15} /> Save</button>
              {map[sel] && <button onClick={clear} data-testid="cal-clear" className="px-4 border border-border rounded-full text-muted-foreground hover:text-primary"><Trash2 size={16} /></button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
